import type { PropsWithChildren, ReactNode } from 'react';
import { Children, cloneElement, isValidElement } from 'react';

import { AccordionItem } from 'components/Accordion/Item/AccordionItem';
import { DropdownChevron } from 'components/Dropdown/Chevron/DropdownChevron';
import { Icon } from 'components/Icon/Icon';
import type { IconName } from 'components/Icon/IconName';
import type { SearchRefinementType } from 'modules/search/constants/refinements/searchRefinementTypes';
import { useSearchAnalytics } from 'modules/search/hooks/useSearchAnalytics';
import type { SearchRefinementVariant } from 'modules/search/types/SearchRefinementVariant';
import { trackEvent } from 'utils/analytics/track/trackEvent';

import {
  SearchFacetAccordionSummary,
  SearchFacetAccordionSummaryLabel,
} from './SearchFacetAccordionItem.styled';

type Props = {
  facetType: SearchRefinementType;
  label: ReactNode;
  icon?: IconName;
};

export function SearchFacetAccordionItem({
  children,
  facetType,
  icon,
  label,
}: PropsWithChildren<Props>) {
  const { getSearchTrackingData } = useSearchAnalytics();

  return (
    <AccordionItem
      onToggle={(open) =>
        open
          ? trackEvent('Expanded Search Filter', {
              filter_name: `${facetType}Facet`,
              ...getSearchTrackingData(),
            })
          : undefined
      }
      qaId={`search-facet-accordion-item-${facetType}`}
      name="search-facet"
      header={
        <SearchFacetAccordionSummary>
          <SearchFacetAccordionSummaryLabel>
            {icon ? <Icon size={18} name={icon} /> : undefined}
            <span>{label}</span>
          </SearchFacetAccordionSummaryLabel>
          <DropdownChevron size={16} />
        </SearchFacetAccordionSummary>
      }
    >
      <div data-facet-type={facetType}>
        {isValidElement<{ variant: SearchRefinementVariant }>(children)
          ? Children.only(cloneElement(children, { variant: 'accordion' }))
          : null}
      </div>
    </AccordionItem>
  );
}
